import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import {
  UserState,
  AppUser,
  FamilyAccount,
  createDefaultUserSession,
  createDefaultUserPreferences,
  createDefaultAppState,
  getDefaultProfilePermissions
} from '../types/user';
import type {
  LoginRequestDto,
  LoginResponseDto,
  UserInfoDto,
} from '../types/api';
import type {
  RefreshTokenResponseDto,
  ProfileGetListResponseDto,
  AdminHistoryGetListResponseDto,
  AdminHistoryGetListItemDto,
} from '../types/api/authentication';
import { ApiService } from '../services/api';
import { StorageInterface } from './childNavStores';
import { AdminResponseStorage } from '../storage/AdminResponseStorage';
import type { AdminResponseDto } from '../types/AdminResponseDto';
import { AccountUserStorage } from '../storage/AccountUserStorage';
import { AccountPracInfoStorage } from '../storage/AccountPracInfoStorage';
import { normalizeAccountPracInfoPayload } from '../services/account';
import type { AuthorizedUserDto } from '../types/AuthorizedUserDto';
import { ConnectionStorage } from '../storage/ConnectionStorage';
import type { ConnectionDto } from '../types/ConnectionDto';
import type { AccountConnectionDto, AccountPracInfoRequestDto, AccountPracInfoResponseDto, AccountUserSettingDto } from '../types/api/service';
import { applyUserSettingsFromList } from '../utils/applyUserSettings';
import { API_CONSTANTS, AUTH_CONSTANTS, AUTH_STORAGE_KEYS, ALL_AUTH_STORAGE_KEY_VALUES } from '../config/constants';
import { useActiveAccountStore } from './activeAccountStore';

interface UserStoreConfig {
  storage: StorageInterface;
  apiService?: ApiService;
  autoLogin?: boolean;
}

/**
 * Maps UserInfoDto from backend to AppUser for client state
 */
function mapUserInfoDtoToAppUser(userInfo: UserInfoDto, email: string, accessType?: string): AppUser {
  // Enhanced userId mapping with fallback logic
  const mappedUserId = userInfo.sraId || userInfo.accountId || email || 'fallback-user-id';

  return {
    userId: mappedUserId,
    familyId: userInfo.accountId,
    name: userInfo.name,
    email: email,
    role: 'primary', // Default role, could be enhanced based on backend data
    accessType: accessType, // Set the access type from login
    authToken: undefined, // Will be set separately
    refreshToken: undefined, // Will be set separately
    lastLoginDate: new Date().toISOString(),
    preferences: createDefaultUserPreferences(),
    appState: createDefaultAppState(),
    profilePermissions: {},
    createdDate: userInfo.createdAt || new Date().toISOString(),
    lastActiveDate: new Date().toISOString(),
    deviceInfo: {
      platform: (typeof globalThis !== 'undefined' && 'window' in globalThis) ? 'web' as const : 'mobile' as const,
      appVersion: '1.0.0',
    },
  };
}

/**
 * Creates a FamilyAccount from UserInfoDto
 */
function createFamilyAccountFromUserInfo(userInfo: UserInfoDto): FamilyAccount {
  return {
    familyId: userInfo.accountId,
    familyName: `${userInfo.name}'s Family`, // Default name
    accountTier: 'standard', // Default tier
    subscriptionStatus: userInfo.isActive === '1' ? 'active' : 'inactive',
    createdDate: userInfo.createdAt || new Date().toISOString(),
    lastActiveDate: new Date().toISOString(),
    settings: {
      dataRetentionDays: 365,
      allowDataSharing: false,
      backupEnabled: true,
      syncEnabled: true,
    },
  };
}

/**
 * Converts an AccountConnectionDto (numeric API codes) to the ProfileConnectionDto
 * (human-readable strings) used in ChildProfile.connections.
 */
function accountConnDtoToProfileConn(dto: AccountConnectionDto) {
  return {
    ConnId: dto.PatConnId || '',
    DName: dto.DName,
    Email: dto.Type === '0' ? dto.Link : '',
    Type: dto.Type === '1' ? 'Linked' : 'Secure Email',
    Permissions:
      dto.Permissions === '2' ? 'Full Access' :
      dto.Permissions === '1' ? 'Assessments & Reports' : 'Notifications Only',
    Primary: dto.Primary === '1' ? 'true' : '',
    Status: dto.Status,
  };
}

/**
 * Applies connections from AccountGetInfoResponseDto.connections to the profile store.
 * Groups connections by ProfileId and calls updateProfile for each profile.
 * Non-fatal — logs warnings but never throws.
 */
function applyAccountConnectionsToProfileStore(
  connections: AccountConnectionDto[] | undefined,
  profileStore: any,
  callerLabel = 'unknown'
): void {
  console.log(`🔗 applyAccountConnections [${callerLabel}]: connections=${connections?.length ?? 0}, storeAvailable=${!!profileStore}`);
  if (!connections?.length || !profileStore) {
    if (!connections?.length) console.log(`🔗 applyAccountConnections [${callerLabel}]: no connections to apply`);
    if (!profileStore) console.warn(`🔗 applyAccountConnections [${callerLabel}]: profileStore is null/undefined — skipping`);
    return;
  }
  try {
    const byProfile = new Map<string, AccountConnectionDto[]>();
    for (const conn of connections) {
      if (!conn.ProfileId) {
        console.warn(`🔗 applyAccountConnections [${callerLabel}]: connection ${conn.PatConnId} has no ProfileId — skipping`);
        continue;
      }
      const list = byProfile.get(conn.ProfileId) ?? [];
      list.push(conn);
      byProfile.set(conn.ProfileId, list);
    }
    console.log(`🔗 applyAccountConnections [${callerLabel}]: grouped into ${byProfile.size} profile(s):`, Array.from(byProfile.keys()));
    const storeState = profileStore.getState?.();
    if (!storeState || typeof storeState.updateProfile !== 'function') {
      console.warn(`🔗 applyAccountConnections [${callerLabel}]: storeState or updateProfile unavailable`);
      return;
    }
    const storeProfileIds = (storeState.profiles ?? []).map((p: any) => p.id);
    console.log(`🔗 applyAccountConnections [${callerLabel}]: store has ${storeProfileIds.length} profile(s):`, storeProfileIds);
    let appliedCount = 0;
    for (const [profileId, conns] of byProfile.entries()) {
      const exists = storeProfileIds.includes(profileId);
      if (!exists) {
        console.warn(`🔗 applyAccountConnections [${callerLabel}]: profile ${profileId} NOT found in store — connection(s) not applied`);
        continue;
      }
      const mapped = conns.map(accountConnDtoToProfileConn);
      storeState.updateProfile(profileId, { connections: mapped });
      appliedCount++;
      console.log(`✅ applyAccountConnections [${callerLabel}]: Applied ${mapped.length} connection(s) to profile ${profileId}`, mapped.map(c => ({ id: c.ConnId, type: c.Type, name: c.DName })));
    }
    console.log(`🔗 applyAccountConnections [${callerLabel}]: done — applied to ${appliedCount}/${byProfile.size} profile(s)`);
  } catch (err) {
    console.warn(`⚠️ UserStore: applyAccountConnectionsToProfileStore [${callerLabel}] failed (non-fatal):`, err);
  }
}

// User store implementation
export const createUserStore = (config: UserStoreConfig) => {
  return create<UserState>()(
    subscribeWithSelector((set, get) => ({
      // Current user session
      session: createDefaultUserSession(),
      
      // Family management
      familyUsers: [],
      
      // Authentication actions
      login: async (
        email: string,
        password: string,
        name?: string,
        signature?: string,
        accessType?: string,
        skinType?: string,
        profileStore?: any,  // Optional profile store for sync after login
        clientId?: string
      ): Promise<boolean> => {
        try {
          set((state) => ({ ...state, session: { ...state.session, isLoading: true } }));

          // Prepare login request
          const loginRequest: LoginRequestDto = {
            Email: email,
            Name: name || email,
            Password: password,
            Signature: signature || 'testsignature123',
            AccessType: accessType || API_CONSTANTS.DEFAULT_ACCESS_TYPE,
            SkinType: skinType || '1',
            ClientId: clientId || AUTH_CONSTANTS.DEFAULT_AUTH_CLIENT_ID,
          };

          // Authenticate with backend
          if (!config.apiService) {
            throw new Error('ApiService not configured');
          }
          const loginResponse: LoginResponseDto = await config.apiService.authenticate(loginRequest);

          if (loginResponse.success && loginResponse.data) {
            await get().applyAuthSession({
              token: loginResponse.data.token,
              refreshToken: loginResponse.data.refreshToken,
              email,
              userInfo: loginResponse.data.user,
              accessType: loginRequest.AccessType,
              settings: loginResponse.data.settings ?? (loginResponse.data as any).Settings ?? [],
              authorizedUsersData: loginResponse.data.authorizedUsers,
              connectionsData: loginResponse.data.connections,
            });

            return true;
          } else {
            set((state) => ({ ...state, session: { ...state.session, isLoading: false } }));
            return false;
          }
        } catch (error) {
          console.error('Login failed:', error);
          return false;
        }
      },

      applyAuthSession: async (params: {
        token: string;
        refreshToken: string;
        email: string;
        userInfo: UserInfoDto;
        accessType: string;
        settings?: AccountUserSettingDto[];
        authorizedUsersData?: { authorizedusers?: any[] } | null;
        connectionsData?: { connections?: any[] } | null;
        runBootSequence?: boolean;
      }): Promise<{ appUser: AppUser; familyAccount: FamilyAccount }> => {
        const {
          token,
          refreshToken,
          email,
          userInfo,
          accessType,
          settings,
          authorizedUsersData,
          connectionsData,
          runBootSequence = true,
        } = params;

        // Map backend user data to client models
        const appUser = mapUserInfoDtoToAppUser(userInfo, email, accessType);
        const familyAccount = createFamilyAccountFromUserInfo(userInfo);

        // Set auth tokens
        appUser.authToken = token;
        appUser.refreshToken = refreshToken;

        // Update API service with token
        if (config.apiService) {
          config.apiService.setAuthToken(token);
        }

        // Store tokens securely
        await config.storage.setItem(AUTH_STORAGE_KEYS.TOKEN, token);
        await config.storage.setItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
        await config.storage.setItem(AUTH_STORAGE_KEYS.TIMESTAMP, new Date().toISOString());
        await config.storage.setItem(AUTH_STORAGE_KEYS.USER_EMAIL, email);
        await config.storage.setItem(AUTH_STORAGE_KEYS.ACCESS_TYPE, accessType);
        await config.storage.setItem(AUTH_STORAGE_KEYS.NAV_RESET_ON_SIGNIN, '1');

        // Apply and persist per-user settings supplied directly in the auth response.
        // This allows tabs and feature flags to reflect backend settings immediately.
        const sessionSettings = settings ?? [];
        try {
          await config.storage.setItem(AUTH_STORAGE_KEYS.USER_SETTINGS, JSON.stringify(sessionSettings));
          await applyUserSettingsFromList(sessionSettings, config.storage);
        } catch (settingsError) {
          console.warn('⚠️ UserStore: Login settings apply failed (non-fatal):', settingsError);
        }

        // Update session state
        set((state) => ({
          ...state,
          session: {
            user: appUser,
            familyAccount: familyAccount,
            isAuthenticated: true,
            isLoading: false,
            sessionStartTime: new Date().toISOString(),
            lastActivityTime: new Date().toISOString(),
            sessionTimeoutMinutes: 120,
            isOnline: true,
            pendingSyncOperations: 0,
          },
          familyUsers: [appUser],
        }));

        // Debug: Verify the session was set correctly
        const currentState = get();
        console.log('🔐 UserStore: Login successful, user session:', {
          userId: currentState.session?.user?.userId,
          email: currentState.session?.user?.email,
          familyId: currentState.session?.user?.familyId,
          accessType: currentState.session?.user?.accessType,
        });

        // Persist user session
        await config.storage.setItem(AUTH_STORAGE_KEYS.USER_SESSION, JSON.stringify({
          user: appUser,
          familyAccount: familyAccount,
          isAuthenticated: true,
        }));

        // Sync authorized users if available in the auth response
        if (authorizedUsersData?.authorizedusers && authorizedUsersData.authorizedusers.length > 0) {
          try {
            const authorizedUserStorage = new AccountUserStorage(config.storage);
            for (const rawUser of authorizedUsersData.authorizedusers) {
              const dto = AccountUserStorage.convertFromBackendItem(rawUser, appUser.familyId);
              await authorizedUserStorage.saveAccountUser(appUser.familyId, dto);
            }
          } catch (authUsersError) {
            console.warn('⚠️ UserStore: Authorized users sync encountered issues but login continues', authUsersError);
          }
        } else {
          console.log('💡 UserStore: No authorized users available for sync in login response');
        }

        // Sync connections if available in the auth response
        if (connectionsData?.connections && connectionsData.connections.length > 0) {
          try {
            const connectionStorage = new ConnectionStorage(config.storage);
            for (const rawConn of connectionsData.connections) {
              const dto = ConnectionStorage.convertFromBackendItem(rawConn, appUser.familyId);
              await connectionStorage.saveConnection(appUser.familyId, dto);
            }
          } catch (connError) {
            console.warn('⚠️ UserStore: Connections sync encountered issues but login continues', connError);
          }
        } else {
          console.log('💡 UserStore: No connections available for sync in login response');
        }

        // Fetch provider_account_get (AccountSettings only) after login
        try {
          console.log('🔄 UserStore: Fetching provider_account_get(AccountSettings) after login...');
          const accountInfoRequest: AccountPracInfoRequestDto = {
            AccountId: appUser.familyId,
            Types: ['AccountSettings'],
          };
          const accountInfoResponse = await config.apiService!.processServiceRequestWithRefresh(
            {
              RequestType: 'provider_account_get',
              Payload: JSON.stringify(accountInfoRequest),
            },
            'sync',
            { userStore: get, storage: config.storage }
          );
          const accountInfoPayload =
            (accountInfoResponse as any)?.payload
            ?? (accountInfoResponse as any)?.Payload
            ?? (accountInfoResponse as any)?.result
            ?? (accountInfoResponse as any)?.Result
            ?? (accountInfoResponse as any)?.data
            ?? (accountInfoResponse as any)?.Data
            ?? null;
          if (accountInfoResponse?.success && accountInfoPayload !== null && accountInfoPayload !== undefined && accountInfoPayload !== '') {
            const accountInfoDto = normalizeAccountPracInfoPayload(accountInfoPayload) as AccountPracInfoResponseDto;
            const accountPracInfoStorage = new AccountPracInfoStorage(config.storage);
            await accountPracInfoStorage.saveAccountPracInfo(appUser.familyId, accountInfoDto);

            // Dispatch event to refresh display settings based on new AccountSettings
            if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
              console.log('UserStore: dispatching progroups:sra-config-updated after login for accountId:', appUser.familyId);
              window.dispatchEvent(new CustomEvent('progroups:sra-config-updated', {
                detail: { accountId: appUser.familyId },
              }));
            }
          } else {
            console.warn('⚠️ UserStore: provider_account_get returned no data after login');
          }
        } catch (accountInfoError) {
          // Non-fatal — settings bootstrap failure must not block login
          console.warn('⚠️ UserStore: provider_account_get fetch failed after login (non-fatal):', accountInfoError);
        }

        // Initialize active account store
        try {
          const accountPracInfoStorage = new AccountPracInfoStorage(config.storage);
          const accountInfo = await accountPracInfoStorage.getAccountPracInfo(appUser.familyId);
          const accountName = accountInfo?.AccountSettings?.PracInfo?.DName || `Account ${appUser.familyId}`;

          const accessTypeNum = parseInt((appUser.accessType || '10').trim(), 10);
          const canSwitchAccount = !isNaN(accessTypeNum) && accessTypeNum >= 20 && accessTypeNum < 30;

          useActiveAccountStore.getState().setSignInAccount(
            appUser.familyId,
            accountName,
            canSwitchAccount
          );

          console.log('✅ UserStore: Initialized active account store:', {
            signInAccountId: appUser.familyId,
            activeAccountId: appUser.familyId,
            accountName,
            canSwitchAccount,
          });
        } catch (activeAccountError) {
          console.warn('⚠️ UserStore: Failed to initialize active account store (non-fatal):', activeAccountError);
        }

        // Align cold-start sign-in with startup restore using a shared boot runner.
        if (runBootSequence) {
          try {
            const globalStores = (globalThis as any).__proGroupsStores ?? (globalThis as any).__childNavStores;
            if (globalStores?.executeBootSequence) {
              await globalStores.executeBootSequence('login');
            } else if (globalStores?.initializeApp) {
              await globalStores.initializeApp();
            }
          } catch (initError) {
            console.warn('⚠️ UserStore: Post-login boot sequence replay failed (non-fatal):', initError);
          }
        }

        return { appUser, familyAccount };
      },

      logout: async (): Promise<void> => {
        try {
          // Get account identifiers and user ID before clearing session (needed for cache cleanup)
          const currentState = get();
          const accountId = currentState.session?.user?.familyId || currentState.session?.familyAccount?.familyId;
          const userId = currentState.session?.user?.userId || currentState.session?.user?.email;
          
          console.log('🔐 UserStore: Starting sign out and PHI cache cleanup', { accountId, userId });
          
          // Clear API token
          if (config.apiService) {
            config.apiService.setAuthToken(null);
          }
          
          // Clear stored authentication data — the full AUTH_STORAGE_KEYS set, not
          // just the ones a prior pass happened to remove, so logout can never
          // leave stale session state (auth_timestamp, auth_access_type, etc.) behind.
          for (const key of ALL_AUTH_STORAGE_KEY_VALUES) {
            await config.storage.removeItem(key);
          }
          console.log('🔐 UserStore: Cleared authentication tokens');
          
          // Reset active account store
          try {
            useActiveAccountStore.getState().reset();
            console.log('🔐 UserStore: Reset active account store');
          } catch (activeAccountError) {
            console.warn('⚠️ UserStore: Failed to reset active account store (non-fatal):', activeAccountError);
          }
          
          // Clear PHI-related cache (HIPAA compliance - no patient data after sign out)
          if (accountId) {
            // Clear account practice info (SRA settings, display settings)
            const pracInfoStorage = new AccountPracInfoStorage(config.storage);
            await pracInfoStorage.clearAccountPracInfo(accountId);
            console.log('🔐 UserStore: Cleared account practice info for accountId:', accountId);
          }
          
          // Clear ProGroups UI state (contains Clients tab profiles[] cache with PHI)
          // Key pattern: pro-groups-ui-state-v1:* (can be 'anonymous' or userId/email)
          // Data stored in IndexedDB via WebStorageAdapter - must enumerate and clear all matching keys
          const uiStatePrefix = 'pro-groups-ui-state-v1:';
          try {
            // Type guard: check if storage has getAllKeys method (WebStorageAdapter does)
            const storage = config.storage as any;
            if (typeof storage.getAllKeys === 'function') {
              const allKeys = await storage.getAllKeys();
              const uiStateKeys = allKeys.filter((key: string) => key.startsWith(uiStatePrefix));
              for (const key of uiStateKeys) {
                await config.storage.removeItem(key);
                console.log('🔐 UserStore: Cleared ProGroups UI state key:', key);
              }
              if (uiStateKeys.length === 0) {
                console.log('🔐 UserStore: No ProGroups UI state keys found in storage');
              }
            } else {
              // Fallback: try clearing common patterns
              console.warn('🔐 UserStore: Storage does not support getAllKeys, using fallback');
              const fallbackKeys = [
                `${uiStatePrefix}anonymous`,
                userId ? `${uiStatePrefix}${userId}` : null,
              ].filter(Boolean) as string[];
              for (const key of fallbackKeys) {
                await config.storage.removeItem(key);
                console.log('🔐 UserStore: Cleared ProGroups UI state key (fallback):', key);
              }
            }
          } catch (error) {
            console.error('🔐 UserStore: Error clearing ProGroups UI state', error);
          }
          
          // Clear Account tab session latches (global registries that persist across sign outs)
          // These registries control whether background fetches happen on Account tab clicks
          // Must be cleared so new account can fetch its own data
          try {
            const globalScope = globalThis as any;
            delete globalScope.__proGroupsAccountPracInfoPrefetchedByAccountId;
            delete globalScope.__proGroupsAccountPracInfoHydratedByAccountId;
            delete globalScope.__proGroupsAccountPracInfoInFlightByAccountId;
            console.log('🔐 UserStore: Cleared Account tab session latches');
          } catch (error) {
            console.warn('🔐 UserStore: Error clearing Account tab latches', error);
          }
          
          // Note: Pro-groups uses provider_account_get for SRA list (not AccountUserStorage).
          // Profile data comes from provider_profile_search per session (not local storage).
          // Child-nav Zustand profile stores (child-nav-profiles, milestones, etc.) don't exist in pro-groups.
          // AccountUserStorage and ConnectionStorage are child-nav remnants not used in pro-groups.
          
          console.log('🔐 UserStore: PHI cache cleanup complete');
          
          // Reset to default session
          set((state) => ({
            ...state,
            session: createDefaultUserSession(),
            familyUsers: []
          }));
        } catch (error) {
          console.error('Sign out error:', error);
          // Even if storage fails, reset the session
          set((state) => ({
            ...state,
            session: createDefaultUserSession(),
            familyUsers: []
          }));
        }
      },
      
      // Background authentication check
      checkStoredAuth: async (): Promise<boolean> => {
        try {
          console.log('🔍 UserStore: checkStoredAuth called');
          
          const storedToken = await config.storage.getItem('auth_token');
          const storedEmail = await config.storage.getItem('user_email');
          const storedSession = await config.storage.getItem('userSession');
          const storedRefreshToken = await config.storage.getItem('refresh_token');
          const storedAccessType = await config.storage.getItem('auth_access_type');
          
          if (storedToken && storedEmail && storedSession) {
            // Set token in API service if available
            if (config.apiService) {
              config.apiService.setAuthToken(storedToken);
            }
            
            // Restore session from storage
            const sessionData = JSON.parse(storedSession);
            
            // Check if user object needs mapping (old structure to new structure)
            if (sessionData.user) {
              // If user object has old structure (id instead of userId), map it
              const userObj = sessionData.user as any;
              if (!userObj.userId && userObj.id) {
                
                // Map old structure to new
                sessionData.user = {
                  ...userObj,
                  userId: userObj.id,          // Map id -> userId
                  familyId: userObj.accountId, // Map accountId -> familyId
                };
              } else {
                console.log('✅ UserStore: User object already has new structure');
              }
              
              // Ensure the user object has the current tokens (both auth and refresh)
              sessionData.user.authToken = storedToken;
              if (storedRefreshToken) {
                sessionData.user.refreshToken = storedRefreshToken;
              }
              
              // Ensure accessType is set (restore from storage if not in session)
              if (!sessionData.user.accessType && storedAccessType) {
                console.log('🔧 UserStore: Restoring accessType from storage:', storedAccessType);
                sessionData.user.accessType = storedAccessType;
              }
            }
             
            const restoredSession = {
              user: sessionData.user,
              familyAccount: sessionData.familyAccount,
              isAuthenticated: true,
              isLoading: false,
              sessionStartTime: sessionData.sessionStartTime || new Date().toISOString(),
              lastActivityTime: new Date().toISOString(),
              sessionTimeoutMinutes: 120,
              isOnline: true,
              pendingSyncOperations: 0,
            };
            
            set((state) => ({
              ...state,
              session: restoredSession,
              familyUsers: sessionData.user ? [sessionData.user] : [],
            }));

            // Restore active account store
            try {
              const storedActiveAccountId = await config.storage.getItem('active_account_id');
              const storedActiveAccountName = await config.storage.getItem('active_account_name');
              
              const signInAccountId = sessionData.user?.familyId || '';
              const accessTypeNum = parseInt((sessionData.user?.accessType || '10').trim(), 10);
              const canSwitchAccount = !isNaN(accessTypeNum) && accessTypeNum >= 20 && accessTypeNum < 30;
              
              // For SRA users, restore last active account; for Pro users, force to sign-in account
              const activeAccountId = canSwitchAccount && storedActiveAccountId ? storedActiveAccountId : signInAccountId;
              const activeAccountName = canSwitchAccount && storedActiveAccountName ? storedActiveAccountName : sessionData.user?.name || `Account ${signInAccountId}`;
              
              useActiveAccountStore.getState().setSignInAccount(
                signInAccountId,
                sessionData.user?.name || `Account ${signInAccountId}`,
                canSwitchAccount
              );
              
              // If different from sign-in account, set active account separately
              if (canSwitchAccount && activeAccountId !== signInAccountId) {
                useActiveAccountStore.getState().setActiveAccount(activeAccountId, activeAccountName);
              }
              
              console.log('✅ UserStore: Restored active account store:', {
                signInAccountId,
                activeAccountId,
                activeAccountName,
                canSwitchAccount,
              });
            } catch (activeAccountError) {
              console.warn('⚠️ UserStore: Failed to restore active account store (non-fatal):', activeAccountError);
            }

            return true;
          }
          
          return false;
        } catch (error) {
          console.error('Stored auth check error:', error);
          return false;
        }
      },
      
      restoreSessionFromStorage: async (): Promise<boolean> => {
        try {
          const sessionData = await config.storage.getItem('userSession');
          const storedToken = await config.storage.getItem('auth_token');
          const storedRefreshToken = await config.storage.getItem('refresh_token');
          const storedAccessType = await config.storage.getItem('auth_access_type');
          
          if (sessionData && storedToken) {
            const parsed = JSON.parse(sessionData);
            
            // Apply old→new structure mapping (same as checkStoredAuth)
            // Stored sessions may have {id, accountId} instead of {userId, familyId}
            if (parsed.user) {
              const userObj = parsed.user as any;
              if (!userObj.userId && userObj.id) {
                parsed.user = {
                  ...userObj,
                  userId: userObj.id,
                  familyId: userObj.accountId,
                };
              }
              // Always ensure current tokens are attached
              parsed.user.authToken = storedToken;
              if (storedRefreshToken) {
                parsed.user.refreshToken = storedRefreshToken;
              }
              // Ensure accessType is set (restore from storage if not in session)
              if (!parsed.user.accessType && storedAccessType) {
                console.log('🔧 UserStore: Restoring accessType in restoreSessionFromStorage:', storedAccessType);
                parsed.user.accessType = storedAccessType;
              }
            }
            
            set((state) => ({
              session: {
                ...state.session,
                user: parsed.user,
                familyAccount: parsed.familyAccount,
                isAuthenticated: true,
                lastActivityTime: new Date().toISOString(),
                sessionStartTime: parsed.sessionStartTime || new Date().toISOString(),
                sessionTimeoutMinutes: 120,
                isOnline: true,
                pendingSyncOperations: 0,
              },
              familyUsers: parsed.user ? [parsed.user] : [],
            }));
            
            // IMPORTANT: Also refresh the API service auth token
            if (config.apiService) {
              config.apiService.setAuthToken(storedToken);
            }
            
            // Restore active account store
            try {
              const storedActiveAccountId = await config.storage.getItem('active_account_id');
              const storedActiveAccountName = await config.storage.getItem('active_account_name');
              
              const signInAccountId = parsed.user?.familyId || '';
              const accessTypeNum = parseInt((parsed.user?.accessType || '10').trim(), 10);
              const canSwitchAccount = !isNaN(accessTypeNum) && accessTypeNum >= 20 && accessTypeNum < 30;
              
              // For SRA users, restore last active account; for Pro users, force to sign-in account
              const activeAccountId = canSwitchAccount && storedActiveAccountId ? storedActiveAccountId : signInAccountId;
              const activeAccountName = canSwitchAccount && storedActiveAccountName ? storedActiveAccountName : parsed.user?.name || `Account ${signInAccountId}`;
              
              useActiveAccountStore.getState().setSignInAccount(
                signInAccountId,
                parsed.user?.name || `Account ${signInAccountId}`,
                canSwitchAccount
              );
              
              // If different from sign-in account, set active account separately
              if (canSwitchAccount && activeAccountId !== signInAccountId) {
                useActiveAccountStore.getState().setActiveAccount(activeAccountId, activeAccountName);
              }
              
              console.log('✅ UserStore: Restored active account store in restoreSessionFromStorage:', {
                signInAccountId,
                activeAccountId,
                activeAccountName,
                canSwitchAccount,
              });
            } catch (activeAccountError) {
              console.warn('⚠️ UserStore: Failed to restore active account store in restoreSessionFromStorage (non-fatal):', activeAccountError);
            }
            
            return true;
          }
          return false;
        } catch (error) {
          console.error('Auth refresh failed:', error);
          return false;
        }
      },
      
      // Helper method to access storage items
      getStoredItem: async (key: string): Promise<string | null> => {
        try {
          return await config.storage.getItem(key);
        } catch (error) {
          console.error(`Error getting stored item ${key}:`, error);
          return null;
        }
      },
      
      // User management
      updateUser: async (updates: Partial<AppUser>): Promise<void> => {
        const currentUser = get().session.user;
        if (!currentUser) return;
        
        const updatedUser = { ...currentUser, ...updates, lastActiveDate: new Date().toISOString() };
        
        set((state) => ({
          session: {
            ...state.session,
            user: updatedUser,
            lastActivityTime: new Date().toISOString(),
          },
          familyUsers: state.familyUsers.map(user => 
            user.userId === updatedUser.userId ? updatedUser : user
          ),
        }));
        
        // Persist updated user
        const sessionData = await config.storage.getItem('userSession');
        if (sessionData) {
          const parsed = JSON.parse(sessionData);
          parsed.user = updatedUser;
          await config.storage.setItem('userSession', JSON.stringify(parsed));
        }
      },
      
      updatePreferences: async (preferences: Partial<AppUser['preferences']>): Promise<void> => {
        const currentUser = get().session.user;
        if (!currentUser) return;
        
        await get().updateUser({
          preferences: { ...currentUser.preferences, ...preferences }
        });
      },
      
      updateAppState: async (appState: Partial<AppUser['appState']>): Promise<void> => {
        const currentUser = get().session.user;
        if (!currentUser) return;
        
        await get().updateUser({
          appState: { ...currentUser.appState, ...appState }
        });
      },
      
      // Family management
      inviteUser: async (email: string, role: AppUser['role']): Promise<string> => {
        // TODO: Implement user invitation logic
        return 'invitation-id-placeholder';
      },
      
      removeUser: async (userId: string): Promise<void> => {
        set((state) => ({
          familyUsers: state.familyUsers.filter(user => user.userId !== userId),
        }));
      },
      
      updateUserRole: async (userId: string, role: AppUser['role']): Promise<void> => {
        set((state) => ({
          familyUsers: state.familyUsers.map(user => 
            user.userId === userId 
              ? { ...user, role, lastActiveDate: new Date().toISOString() }
              : user
          ),
        }));
      },
      
      // Profile permissions
      updateProfilePermissions: async (profileId: string, permissions: AppUser['profilePermissions'][string]): Promise<void> => {
        const currentUser = get().session.user;
        if (!currentUser) return;
        
        await get().updateUser({
          profilePermissions: {
            ...currentUser.profilePermissions,
            [profileId]: permissions,
          }
        });
      },
      
      canAccessProfile: (profileId: string): boolean => {
        const currentUser = get().session.user;
        if (!currentUser) return false;
        
        const permissions = currentUser.profilePermissions[profileId];
        return permissions?.canView ?? false;
      },
      
      canEditProfile: (profileId: string): boolean => {
        const currentUser = get().session.user;
        if (!currentUser) return false;
        
        const permissions = currentUser.profilePermissions[profileId];
        return permissions?.canEdit ?? false;
      },
      
      // Session management
      extendSession: (): void => {
        set((state) => ({
          session: {
            ...state.session,
            lastActivityTime: new Date().toISOString(),
          },
        }));
      },
      
      isSessionValid: (): boolean => {
        const session = get().session;
        if (!session.isAuthenticated || !session.user) return false;
        
        const now = new Date().getTime();
        const lastActivity = new Date(session.lastActivityTime).getTime();
        const timeoutMs = session.sessionTimeoutMinutes * 60 * 1000;
        
        return (now - lastActivity) < timeoutMs;
      },
      
      checkSessionTimeout: (): boolean => {
        const isValid = get().isSessionValid();
        if (!isValid && get().session.isAuthenticated) {
          // Auto-logout on timeout
          get().logout();
        }
        return isValid;
      },
      
      // Utility getters
      getCurrentUser: (): AppUser | null => {
        return get().session.user;
      },
      
      getFamilyAccount: (): FamilyAccount | null => {
        return get().session.familyAccount;
      },
      
      getSelectedProfileId: (): string | null => {
        const currentUser = get().session.user;
        return currentUser?.appState.selectedProfileId ?? null;
      },
      
      isPremiumUser: (): boolean => {
        const familyAccount = get().session.familyAccount;
        return familyAccount?.accountTier === 'premium';
      },
      
      canInviteUsers: (): boolean => {
        const currentUser = get().session.user;
        return currentUser?.role === 'primary';
      },
      
      // Configuration management
      setApiService: (apiService: ApiService): void => {
        config.apiService = apiService;
      },
      
      getApiService: (): ApiService | null => {
        return config.apiService || null;
      },

      // Profile synchronization - Common function for login and token refresh
      syncProfilesFromResponse: async (
        profileData?: ProfileGetListResponseDto,
        adminHistoryData?: AdminHistoryGetListResponseDto,
        accountId?: string,
        profileStore?: any
      ): Promise<{ success: boolean; message: string; syncResults?: any }> => {
        void profileData;
        void adminHistoryData;
        void accountId;
        void profileStore;
        return {
          success: true,
          message: 'Legacy profile sync disabled for Pro Groups.',
          syncResults: { skipped: true },
        };
      },

      // Admin History synchronization - Common function for login and token refresh
      syncAdminHistoryFromResponse: async (
        adminHistoryData?: AdminHistoryGetListResponseDto,
        accountId?: string
      ): Promise<{ success: boolean; message: string; syncResults?: any }> => {
        void adminHistoryData;
        void accountId;
        return {
          success: true,
          message: 'Legacy admin-history sync disabled for Pro Groups.',
          syncResults: { skipped: true },
        };
      },

      // Common backend synchronization function - used by login, token refresh, and debug operations
      syncBackendFromResponse: async (options: {
        profileData?: ProfileGetListResponseDto;
        adminHistoryData?: AdminHistoryGetListResponseDto;
        familyId?: string;
        profileStore?: any;
        contextDescription: string; // 'login', 'token_refresh', 'debug', etc.
      }): Promise<{ 
        success: boolean; 
        message: string; 
        results: {
          profile?: { success: boolean; message: string; syncResults?: any };
          adminHistory?: { success: boolean; message: string; syncResults?: any };
        };
        errors: string[];
      }> => {
        void options;
        return {
          success: true,
          message: 'Legacy backend profile/admin sync disabled for Pro Groups.',
          results: {
            profile: { success: true, message: 'Skipped' },
            adminHistory: { success: true, message: 'Skipped' },
          },
          errors: [],
        };
      },
      
      // State reset
      reset: (): void => {
        set(() => ({
          session: createDefaultUserSession(),
          familyUsers: [],
        }));
      },
    }))
  );
};

// Export type for the user store
export type UserStore = ReturnType<typeof createUserStore>;